/**
 * BCC France — Highlight Slider module.
 *
 * Manages pagination for the featured information carousel on the home page.
 * Features:
 * - Auto-advances to the next slide every 2500ms
 * - Infinite loop carousel
 * - Touch swipe support (left/right)
 * - Progress bar indicator
 *
 * Markup structure:
 *   .highlight-slider
 *     .highlight-slider__track
 *       .highlight-slider__slide (one per info)
 *     .highlight-slider__progress
 */

export function initHighlightSlider() {
  const slider = document.querySelector('.highlight-slider');
  if (!slider) return;

  const track = slider.querySelector('.highlight-slider__track');
  const slides = slider.querySelectorAll('.highlight-slider__slide');
  const progress = slider.querySelector('.highlight-slider__progress');

  if (!track || slides.length === 0 || !progress) return;

  let currentIndex = 0;
  const slideWidth = 100; // Each slide takes 100% width
  const autoSlideInterval = 2500; // milliseconds
  let autoSlideTimeout;
  let touchStartX = 0;
  let touchEndX = 0;

  /**
   * Update progress bar width based on current slide index
   */
  function updateProgress() {
    const progressPercent = ((currentIndex + 1) / slides.length) * 100;
    progress.style.width = progressPercent + '%';
  }

  /**
   * Show slide at given index (infinite loop)
   */
  function showSlide(index) {
    // Modulo for infinite loop
    currentIndex = index % slides.length;

    // Translate track to show the correct slide
    const offset = currentIndex * -slideWidth;
    track.style.transform = `translateX(${offset}%)`;

    // Update progress bar
    updateProgress();
  }

  /**
   * Advance to next slide
   */
  function advanceSlide() {
    showSlide(currentIndex + 1);
  }

  /**
   * Go to previous slide
   */
  function previousSlide() {
    showSlide(currentIndex - 1);
  }

  /**
   * Reset and start auto-slide timer
   */
  function resetAutoSlide() {
    clearTimeout(autoSlideTimeout);
    autoSlideTimeout = setInterval(advanceSlide, autoSlideInterval);
  }

  /**
   * Handle touch start
   */
  function handleTouchStart(e) {
    touchStartX = e.changedTouches[0].screenX;
  }

  /**
   * Handle touch end and detect swipe direction
   */
  function handleTouchEnd(e) {
    touchEndX = e.changedTouches[0].screenX;
    const swipeThreshold = 50; // minimum pixels to trigger swipe
    const diff = touchStartX - touchEndX;

    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        // Swiped left → next slide
        advanceSlide();
      } else {
        // Swiped right → previous slide
        previousSlide();
      }
      resetAutoSlide(); // Restart timer on swipe
    }
  }

  // Attach touch event listeners
  track.addEventListener('touchstart', handleTouchStart, false);
  track.addEventListener('touchend', handleTouchEnd, false);

  // Initialize: show first slide and start auto-slide
  showSlide(0);
  resetAutoSlide();
}

